import React, { useState, useEffect } from 'react';

const toMinutes = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const PRAYER_ORDER = ['shacharit', 'mincha', 'maariv', 'other'];
const PRAYER_LABEL = { shacharit: 'שחרית', mincha: 'מנחה', maariv: 'מעריב' };

const PRAYER_ACCENT = {
  shacharit: '#1e3a5f',
  mincha:    '#b8860b',
  maariv:    '#374151',
  other:     '#64748b',
};

const ZMAN_SHORT = {
  daySunset: 'השקיעה', dayNetz: 'הנץ', dayAlot: 'עלות השחר', dayChatzot: 'חצות',
  dayMinchaGedola: 'מנחה גדולה', dayMinchaKetana: 'מנחה קטנה', dayPlagHamincha: 'פלג המנחה',
  dayTzeit7083: 'צאת הכוכבים', dayTzeit85: 'צאת הכוכבים', dayTzeit42: 'צאת הכוכבים',
  dayTzeit50: 'צאת הכוכבים', dayTzeit72: 'צאת הכוכבים',
  daySofZmanShma: 'סוף זמן שמע', daySofZmanShmaMGA: 'סוף זמן שמע',
  daySofZmanTfilla: 'סוף זמן תפילה', daySofZmanTfillaMGA: 'סוף זמן תפילה',
};
const formatTimeRule = (ct) => {
  if (ct.type === 'fixed') return 'שעה קבועה';
  if (ct.type === 'relative') {
    const label = ZMAN_SHORT[ct.baseTime] || ct.baseTime;
    const offset = parseInt(ct.offset) || 0;
    if (offset === 0) return label;
    const abs = Math.abs(offset);
    return offset < 0 ? `${abs} דק׳ לפני ${label}` : `${abs} דק׳ אחרי ${label}`;
  }
  return null;
};

const DAY_NAMES = { 0: 'א׳', 1: 'ב׳', 2: 'ג׳', 3: 'ד׳', 4: 'ה׳', 5: 'ו׳', roshChodesh: 'ר״ח' };

const todayDow = new Date().getDay();
const todayHebrewDay = (() => {
  try { return parseInt(new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric' }).format(new Date())); }
  catch { return 0; }
})();
const isRoshChodeshToday = todayHebrewDay === 1 || todayHebrewDay === 30;
const isOverrideActive = (days) =>
  (days || []).includes(todayDow) || (isRoshChodeshToday && (days || []).includes('roshChodesh'));

const StatusBox = ({ msg, isError }) => (
  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: 'rtl', fontSize: '1rem', color: isError ? '#c0392b' : '#94a3b8' }}>
    {msg}
  </div>
);

/**
 * Compact — תצוגה צפופה ויעילה שחוסכת מקום.
 * כל תפילה ברצועה אחת: קו צבעוני, שם ושעה באותה שורה.
 * חריגות ימים מופיעות כ-chips קטנים מתחת.
 * מתאים לרכיבים קטנים ולוחות צפופים.
 */
const Compact = ({ processedCustomTimes, loading, error, title = 'תפילות יום חול', showTitle = true, recurringDisplay = 'compact', showRuleMode = false, highlightNextMinyan = false }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  if (loading) return <StatusBox msg="טוען..." />;
  if (error) return <StatusBox msg={error} isError />;
  if (!processedCustomTimes?.length) return <StatusBox msg="אין זמני תפילה" />;

  const nowMin = now.getHours() * 60 + now.getMinutes();

  const sorted = [...processedCustomTimes].sort((a, b) =>
    PRAYER_ORDER.indexOf(a.prayer || 'other') - PRAYER_ORDER.indexOf(b.prayer || 'other')
  );

  let nextIdx = -1;
  if (highlightNextMinyan) {
    let bestMin = Infinity;
    sorted.forEach((ct, i) => {
      if (ct.isActiveToday === false) return;
      const activeOverride = (ct.resolvedOverrides || ct.dayOverrides || []).find(ov => isOverrideActive(ov.days));
      const times = ct.type === 'recurring'
        ? (activeOverride ? [toMinutes(activeOverride.resolvedTime)] : (ct.resolvedSlots || []).map(toMinutes).filter(m => m >= 0))
        : (() => { const m = toMinutes(activeOverride ? activeOverride.resolvedTime : ct.resolvedTime); return m >= 0 ? [m] : []; })();
      for (const m of times) {
        if (m >= nowMin && m < bestMin) { bestMin = m; nextIdx = i; }
      }
    });
  }

  // Group by prayer category for section dividers
  let lastPrayer = null;

  return (
    <div style={{
      width: '100%', height: '100%', direction: 'rtl',
      display: 'flex', flexDirection: 'column',
      backgroundColor: '#fafbfc', boxSizing: 'border-box', overflow: 'hidden',
    }}>
      {showTitle && (
        <div style={{
          padding: '0.625rem 1rem',
          borderBottom: '1px solid #e2e8f0',
          flexShrink: 0,
          backgroundColor: '#ffffff',
        }}>
          <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1e3a5f' }}>{title}</span>
        </div>
      )}

      <div style={{
        flex: 1, overflowY: 'auto',
        padding: '0.5rem',
        display: 'flex', flexDirection: 'column',
        gap: '0',
        boxSizing: 'border-box',
      }}>
        {sorted.map((ct, idx) => {
          const label = ct.label || PRAYER_LABEL[ct.prayer] || '';
          const accent = PRAYER_ACCENT[ct.prayer] || PRAYER_ACCENT.other;
          const isRecurring = ct.type === 'recurring';
          const rule = !isRecurring ? formatTimeRule(ct) : null;
          const overrides = ct.resolvedOverrides || ct.dayOverrides || [];
          const activeOverride = overrides.find(ov => isOverrideActive(ov.days));
          const isNext = highlightNextMinyan && idx === nextIdx;

          // Show section divider when prayer type changes
          const showDivider = ct.prayer !== lastPrayer && lastPrayer !== null;
          lastPrayer = ct.prayer;

          const mainTime = isRecurring
            ? (ct.resolvedSlots?.length ? `${ct.resolvedSlots[0]}–${ct.resolvedSlots[ct.resolvedSlots.length - 1]}` : '--:--')
            : (activeOverride ? activeOverride.resolvedTime : ct.resolvedTime || '--:--');

          return (
            <React.Fragment key={idx}>
              {showDivider && (
                <div style={{ height: '1px', backgroundColor: '#e2e8f0', margin: '4px 8px' }} />
              )}

              <div style={{
                backgroundColor: isNext ? '#eff6ff' : '#ffffff',
                borderRadius: '8px',
                marginBottom: '3px',
                overflow: 'hidden',
                border: isNext ? '2px solid #1e3a5f' : (activeOverride ? '1px solid #bbf7d0' : '1px solid transparent'),
                boxShadow: isNext ? '0 2px 8px rgba(30,58,95,0.12)' : 'none',
              }}>
                {/* Main row */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem 0.75rem',
                  gap: '8px',
                }}>
                  {/* Color indicator */}
                  <div style={{
                    width: '3px', height: '24px',
                    backgroundColor: accent, borderRadius: '2px',
                    flexShrink: 0,
                  }} />

                  {/* Name */}
                  <span style={{
                    fontSize: '0.9rem', fontWeight: 700,
                    color: activeOverride ? '#15803d' : '#1e293b',
                    flex: 1,
                    whiteSpace: 'nowrap',
                  }}>
                    {label}
                  </span>

                  {/* Recurring badge */}
                  {isRecurring && (
                    <span style={{
                      fontSize: '0.6rem', fontWeight: 600, color: '#166534',
                      backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
                      borderRadius: '99px', padding: '1px 6px',
                      flexShrink: 0, whiteSpace: 'nowrap',
                    }}>
                      כל {ct.interval} דק׳
                    </span>
                  )}

                  {/* Rule or time */}
                  {showRuleMode && ct.type === 'relative' && !isRecurring ? (
                    <span style={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#475569',
                      flexShrink: 0, whiteSpace: 'nowrap',
                    }}>
                      {rule}
                    </span>
                  ) : (
                    <span style={{
                      fontSize: isRecurring ? '0.85rem' : '1.15rem',
                      fontWeight: 800,
                      color: activeOverride ? '#15803d' : '#0f172a',
                      fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                      letterSpacing: '-0.02em',
                      flexShrink: 0, whiteSpace: 'nowrap',
                    }}>
                      {mainTime}
                    </span>
                  )}

                  {/* Subtle rule hint / days label */}
                  {(!showRuleMode && rule && !isRecurring || ct.daysLabel) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 3px', alignItems: 'baseline', flexShrink: 1 }}>
                      {!showRuleMode && rule && !isRecurring && (
                        <span style={{ whiteSpace: 'nowrap', fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>
                          {rule}{ct.daysLabel ? ' ·' : ''}
                        </span>
                      )}
                      {ct.daysLabel && (
                        <span style={{ whiteSpace: 'nowrap', fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>
                          {ct.daysLabel}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Full recurring slots */}
                {isRecurring && recurringDisplay === 'full' && ct.resolvedSlots?.length > 0 && (
                  <div style={{
                    padding: '2px 0.75rem 6px calc(0.75rem + 11px)',
                    display: 'flex', flexWrap: 'wrap', gap: '2px', direction: 'ltr',
                  }}>
                    {ct.resolvedSlots.map((slot, i) => (
                      <span key={i} style={{
                        fontSize: '0.6rem', fontWeight: 600, color: '#64748b',
                        fontVariantNumeric: 'tabular-nums',
                        backgroundColor: '#f8fafc', borderRadius: '3px', padding: '1px 4px',
                      }}>
                        {slot}
                      </span>
                    ))}
                  </div>
                )}

                {/* Day overrides — as inline chips */}
                {overrides.length > 0 && (
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '3px',
                    padding: '0 0.75rem 0.4rem calc(0.75rem + 11px)',
                  }}>
                    {overrides.map((ov, oi) => {
                      const active = isOverrideActive(ov.days);
                      const dayLabel = (ov.days || []).map(d => DAY_NAMES[d]).filter(Boolean).join(' ');
                      const time = ov.resolvedTime || ov.time || '--:--';
                      return (
                        <span key={oi} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          fontSize: '0.65rem',
                          fontWeight: active ? 700 : 400,
                          color: active ? '#15803d' : '#94a3b8',
                          backgroundColor: active ? '#f0fdf4' : '#f8fafc',
                          border: active ? '1px solid #bbf7d0' : '1px solid #f1f5f9',
                          borderRadius: '99px', padding: '1px 7px',
                        }}>
                          {active && (
                            <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#16a34a', display: 'inline-block' }} />
                          )}
                          <span>{dayLabel}</span>
                          <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', direction: 'ltr' }}>{time}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Compact;
